import React from 'react';
import { Skeleton } from '@mui/material';

import {
  SidebarContainer,
  SidebarHeader,
  SidebarContent,
  LoadMoreButton,
  EmptyState,
  ItemContainer,
  TimelineContainer,
  ContentContainer,
  TransactionBadge,
  Amount,
  Description,
  DateTime,
  CreatedBy,
  StatusIcon,
  Connector,
  TopRow,
} from './styles';
import { PaymentStatus, TransactionType } from 'types/enums';
import {
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  RotateCcw,
  Ban,
} from 'lucide-react';
import { ITransactionViewModel } from 'viewModels';

export const getStatusIcon = (status: PaymentStatus) => {
  switch (status) {
    case PaymentStatus.COMPLETED:
      return <CheckCircle className="icon" color="#3fbd45" />;
    case PaymentStatus.PENDING:
      return <Clock className="icon" color="#faa628" />;
    case PaymentStatus.FAILED:
      return <XCircle className="icon" color="#fa0c04" />;
    case PaymentStatus.CANCELLED:
      return <Ban className="icon" color="#fa0c04" />;
    case PaymentStatus.REFUNDED:
      return <RotateCcw className="icon" color="#3fbd45" />;
    case PaymentStatus.PROCESSING:
      return <Clock className="icon" color="#f5f232" />;
    case PaymentStatus.EXPIRED:
      return <AlertCircle className="icon" color="#fd7672" />;
    default:
      return <AlertCircle className="icon" color="#797979" />;
  }
};

interface TransactionSidebarProps {
  transactions: ITransactionViewModel[];
  onTransactionClick: (transaction: ITransactionViewModel) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isFetching?: boolean; // Novo: indica se está carregando as transações
}

interface TransactionItemProps {
  transaction: ITransactionViewModel;
  onClick: () => void;
  showConnector: boolean;
}

export const TransactionSidebar: React.FC<
  TransactionSidebarProps
> = ({
  transactions,
  onTransactionClick,
  onLoadMore,
  hasNextPage,
  isLoadingMore,
  isFetching,
}) => {
  return (
    <SidebarContainer>
      <SidebarHeader>
        <h3>Histórico de Transações</h3>
      </SidebarHeader>

      <SidebarContent>
        {isFetching ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                height={80}
                sx={{
                  borderRadius: 2,
                  mb: 1.5,
                  bgcolor: 'rgba(0,0,0,0.06)',
                }}
                animation="wave"
              />
            ))}
          </>
        ) : transactions.length > 0 ? (
          <>
            {transactions.map((transaction, index) => (
              <TransactionItem
                key={transaction.id}
                transaction={transaction}
                onClick={() => onTransactionClick(transaction)}
                showConnector={index < transactions.length - 1}
              />
            ))}

            {hasNextPage && (
              <LoadMoreButton
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore
                  ? 'Carregando...'
                  : 'Carregar mais transações'}
              </LoadMoreButton>
            )}
          </>
        ) : (
          <EmptyState>
            <div className="empty-icon">📋</div>
            <p>Nenhuma transação encontrada</p>
          </EmptyState>
        )}
      </SidebarContent>
    </SidebarContainer>
  );
};

export const TransactionItem: React.FC<TransactionItemProps> = ({
  transaction,
  onClick,
  showConnector,
}) => {
  const formatDateTime = (dateInput: string | Date) => {
    const date =
      typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return {
      time: date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      date: date.toLocaleDateString('pt-BR'),
    };
  };

  const formatCurrency = (value: number) => {
    return `R$ ${(value / 100).toFixed(2).replace('.', ',')}`;
  };

  const { time, date } = formatDateTime(transaction.createdAt);

  return (
    <ItemContainer onClick={onClick}>
      <TimelineContainer>
        <StatusIcon>{getStatusIcon(transaction.status)}</StatusIcon>
        {showConnector && <Connector />}
      </TimelineContainer>

      <ContentContainer>
        <TopRow>
          <div>
            <TransactionBadge type={transaction.transactionType}>
              {transaction.transactionType === TransactionType.CREDIT
                ? 'CRÉDITO'
                : 'DÉBITO'}
            </TransactionBadge>
            <Amount>{formatCurrency(transaction.amount)}</Amount>
          </div>
          <DateTime>
            <span>{time}</span>
            <span>{date}</span>
          </DateTime>
        </TopRow>
        <Description>{transaction.description}</Description>
        {transaction.metadata?.createdByName && (
          <CreatedBy>
            Criado por: {transaction.metadata.createdByName}
          </CreatedBy>
        )}
      </ContentContainer>
    </ItemContainer>
  );
};
